'use client';

import { useState } from 'react';

import { useRouter } from 'next/navigation';

import { useAuth } from '@/hooks/use-auth';
import { fromFetchError } from '@/lib/api/error';
import { normalizeIdentityNumber } from '@/lib/identityNumber';
import { getReferral } from '@/lib/referral';
import { normalizeSerialNumberInput } from '@/lib/serialNumber';
import { useGetMyProfileQuery } from '@/services/profileApi';
import { useCreateRepairRequestMutation } from '@/services/repairApi';
import { cn, useToast } from '@holistic/components';
import { skipToken } from '@reduxjs/toolkit/query/react';

import type { CreateRepairRequestBody } from '@/types/repair';
import type { PaymentFormValues } from './cargo/payment-schema';
import type { StepLabel, StepParam } from './constants';
import type {
  SummaryRow,
  TalepDevice,
  TalepOption,
  TalepRequestType,
} from './types';

import CallbackRequestModal from './callback-request-modal';
import { cargoPreviewRows, useCargoFlow } from './cargo/use-cargo-flow';
import CompletedScreen from './completed-screen';
import ConfirmStep from './confirm-step';
import { STEP_CONTENT, STEP_PARAMS } from './constants';
import EcoBand from './eco-band';
import OptionGrid from './option-grid';
import RequestTypeStep from './request-type-step';
import SelectionCard from './selection-card';
import StepActions from './step-actions';
import StepHeading from './step-heading';
import TalepStepper from './talep-stepper';
import TrustPanel from './trust-panel';

type TalepFlowProps = {
  steps: StepLabel[];
  step: number;
  paramKey: StepParam | null;
  options: TalepOption[];
  summary: SummaryRow[];
  modelId: string;
  /** Arıza adımının seçenekleri; yalnızca `paramKey === 'type'` iken dolu. */
  requestTypes: TalepRequestType[];
  /** Seçilmiş arızalar (fiyatlarıyla); onay ekranındaki döküm buradan çıkar. */
  selectedTypes: TalepRequestType[];
  /** Yan paneldeki cihaz kartı; marka+model seçilene kadar null. */
  device: TalepDevice | null;
  query: string;
};

const TalepFlow = ({
  steps,
  step,
  paramKey,
  options,
  summary,
  modelId,
  requestTypes,
  selectedTypes,
  device,
  query,
}: TalepFlowProps) => {
  const router = useRouter();
  const toast = useToast();
  const { isLoggedIn } = useAuth();

  const initialParams = new URLSearchParams(query);

  // Kimlik numarası talebin zorunlu alanı; profilde kayıtlıysa yeniden
  // yazdırmamak için okunuyor. Yalnızca son adımda ve giriş yapılmışsa
  // sorulur — misafir müşterinin zaten profili yok.
  const { data: profile } = useGetMyProfileQuery(
    paramKey || !isLoggedIn ? skipToken : undefined
  );

  const [description, setDescription] = useState('');
  const [serialNumber, setSerialNumber] = useState(
    initialParams.get('serialNumber') ?? ''
  );
  const [serialNumberError, setSerialNumberError] = useState<string | null>(
    null
  );
  // `null` = müşteri alana hiç dokunmadı; o sürece profildeki kayıtlı numara
  // gösterilir. Effect ile kopyalamak yerine türetiliyor ki profil geç gelse de
  // yazılmış bir değerin üzerine binmesin.
  const [identityNumberOverride, setIdentityNumberOverride] = useState<
    string | null
  >(null);
  const identityNumber =
    identityNumberOverride ?? profile?.identityNumber ?? '';
  const [identityNumberError, setIdentityNumberError] = useState<string | null>(
    null
  );
  const [warrantyId, setWarrantyId] = useState<string | null>(
    initialParams.get('warrantyId')
  );
  // Giriş yapmamış müşteri "üye olmadan devam et"i seçtiyse doldurduğu bilgiler;
  // backend bunlarla telefon+TC eşleşmesine göre bir hesap bulur ya da açar.
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestPhoneNumber, setGuestPhoneNumber] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [liveSelectedTypeIds, setLiveSelectedTypeIds] = useState<string[]>(
    selectedTypes.map((t) => t.id)
  );
  const [selectedAnalysisType, setSelectedAnalysisType] =
    useState<TalepRequestType | null>(null);
  // Tek seçimli adımlarda kart tıklaması hem işaretler hem de doğrudan bir
  // sonraki adıma geçer; işaret, sunucu yeni adımı çözene kadar geri bildirim
  // olarak kalır. Seçim adım numarasıyla birlikte tutulur: URL değişince
  // bileşen yeniden bağlanmadığı için, adımı da saklamadan önceki adımın
  // seçimi bir sonraki adıma sızıyordu.
  const [pendingOption, setPendingOption] = useState<{
    step: number;
    id: string;
  } | null>(null);
  const pendingOptionId =
    pendingOption?.step === step ? pendingOption.id : null;

  // Talep POST'u istemciden atılır: gövde tamamen istemci state'inden geliyor
  // ve token'ı `/api` catch-all proxy'si eklediği için server action'a gerek yok.
  const [createRepairRequest, { isLoading: isCreating }] =
    useCreateRepairRequestMutation();

  const isSubmitting = isCreating;

  // Kargo adresi ve randevu günü, tek POST'a kadar yalnızca burada durur.
  const cargo = useCargoFlow();

  const setParam = (key: StepParam, value: string | null) => {
    const params = new URLSearchParams(query);
    const from = STEP_PARAMS.indexOf(key);
    STEP_PARAMS.slice(from).forEach((k) => params.delete(k));
    if (value) params.set(key, value);
    const qs = params.toString();
    router.push(qs ? `/talep?${qs}` : '/talep');
  };

  const returnUrl = `/talep?${query}`;
  const goToLogin = () =>
    router.push(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
  const goToRegister = () =>
    router.push(`/register?returnUrl=${encodeURIComponent(returnUrl)}`);

  /**
   * Talep gövdesi. İki uç da (talep açma ve ödeme başlatma) aynı alanları
   * bekliyor; ödeme ucunda üzerine yalnızca kart alanları ekleniyor.
   */
  const buildRequestBody = (): CreateRepairRequestBody | null => {
    const requestTypeIds = selectedTypes.map((t) => t.id);
    if (requestTypeIds.length === 0) {
      toast.error('En az bir arıza seçilmiş olmalıdır.');
      return null;
    }

    const { address, pickupDate } = cargo;
    if (!address.city || !address.town) {
      toast.error('Teslim alma adresi eksik.');
      return null;
    }

    const referral = getReferral();
    const isTv = summary.some(
      (s) =>
        s.key === 'category' &&
        (s.value.toLowerCase().includes('tv') ||
          s.value.toLowerCase().includes('televizyon'))
    );

    return {
      requestTypeIds,
      // Backend tekil alanı da okuyor; çoklu liste doluyken yok sayıyor.
      requestTypeId: requestTypeIds[0],
      modelId,
      variantId: null,
      serialNumber: serialNumber
        ? normalizeSerialNumberInput(serialNumber)
        : null,
      serialNumberImageUrl: null,
      // Ekran doğrulaması geçtiği için burada 11 haneli rakam olması garanti.
      identityNumber: normalizeIdentityNumber(identityNumber),
      description: description.trim() || null,
      warrantyId: warrantyId || null,
      cargoCity: address.city.name,
      cargoTown: address.town.name,
      cargoDistrict: address.district?.name ?? null,
      cargoAddressLine: address.addressLine.trim(),
      cargoPickupDate: isTv ? null : pickupDate,
      utmSource: referral.utmSource ?? null,
      utmMedium: referral.utmMedium ?? null,
      utmCampaign: referral.utmCampaign ?? null,
      referrer: referral.referrer ?? null,
      landingPage: referral.landingPage ?? null,
    };
  };

  /**
   * Talebi açar. Önceden herhangi bir ödeme alınmaz; tüm talepler doğrudan
   * `repair-request/add` ile oluşturulur. Cihaz atölyeye ulaşıp uzman kontrolü
   * tamamlandıktan sonra onay ve 3D Secure ödeme adımına geçilir.
   */
  const handleSubmit = async (_card?: PaymentFormValues | null) => {
    const body = buildRequestBody();
    if (!body) return;

    // Yalnızca misafirde: proxy token eklemeyeceği için backend kimliği
    // buradan çözer (bkz. GuestCustomerResolver). Login'liyse hiç gönderilmez,
    // JWT geçerli olur.
    const guestFields =
      !isLoggedIn && isGuestMode
        ? {
            firstName: guestFirstName.trim(),
            lastName: guestLastName.trim(),
            email: guestEmail.trim(),
            ...(guestPhoneNumber.trim()
              ? { phoneNumber: guestPhoneNumber }
              : {}),
          }
        : {};

    try {
      const id = await createRepairRequest({
        ...body,
        ...guestFields,
      }).unwrap();

      setCreatedRequestId(id ?? null);
      setCompleted(true);
    } catch (err) {
      const error = fromFetchError(
        err,
        'Talep oluşturulamadı, lütfen tekrar deneyin.'
      );

      // Oturum düştüyse (proxy token ekleyemedi) tek çıkış yolu yeniden giriş.
      if (error.status === 401) {
        toast.error('Giriş yapmanız gerekiyor.');
        router.push(`/login?returnUrl=${encodeURIComponent('/talep')}`);
        return;
      }

      if (
        error.problem?.detail?.includes(
          'does not cover this device or has expired'
        ) ||
        error.message.includes('geçerli bir garanti bulunamadı')
      ) {
        setWarrantyId(null);
      }
      toast.error(error.message);
    }
  };

  const active = completed ? steps.length : step;

  // Arıza adımında seçim burada tutulur; hem ızgara hem yan panel aynı
  // listeyi kullanır, "Devam et" ile tek seferde URL'e yazılır.
  const currentDisplayTypes =
    paramKey === 'type'
      ? requestTypes.filter((r) => liveSelectedTypeIds.includes(r.id))
      : selectedTypes;

  const toggleType = (id: string) =>
    setLiveSelectedTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Bu akışta gerçekten görünen adımların parametreleri. Serisi olmayan
  // markada "Seri" adımı `steps`ten düştüğü için buradan da düşer —
  // sabit STEP_PARAMS üzerinden gidilirse model adımındaki "Geri dön",
  // URL'de zaten olmayan `series`i silmeye çalışıp aynı adımda kalırdı.
  const activeParams = steps
    .map((s) => s.param)
    .filter((p): p is StepParam => !!p);

  const goBack = () => {
    const index = paramKey
      ? activeParams.indexOf(paramKey)
      : activeParams.length;
    const previous = activeParams[index - 1];
    if (previous) setParam(previous, null);
  };

  const stepContent = paramKey ? (
    paramKey === 'type' ? (
      <RequestTypeStep
        options={requestTypes}
        selectedIds={liveSelectedTypeIds}
        onToggle={toggleType}
        onSubmit={() => setParam('type', liveSelectedTypeIds.join(','))}
        onBack={goBack}
        stepNumber={step + 1}
        onSelectAnalysisType={(type) => setSelectedAnalysisType(type)}
      />
    ) : (
      <div className="space-y-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-xs md:p-8">
        <StepHeading
          stepNumber={step + 1}
          title={STEP_CONTENT[paramKey].title}
          description={STEP_CONTENT[paramKey].description}
        />
        <OptionGrid
          options={options}
          selectedId={pendingOptionId}
          onSelect={(id) => {
            setPendingOption({ step, id });
            setParam(paramKey, id);
          }}
          searchPlaceholder={STEP_CONTENT[paramKey].searchPlaceholder}
        />
        {paramKey === 'category' && <EcoBand />}
        <StepActions onBack={step > 0 ? goBack : undefined} />
      </div>
    )
  ) : (
    <ConfirmStep
      summary={summary}
      requestTypes={selectedTypes}
      onEdit={(key) => setParam(key, null)}
      description={description}
      onDescriptionChange={setDescription}
      serialNumber={serialNumber}
      onSerialNumberChange={(val) => {
        setSerialNumber(val);
        setSerialNumberError(null);
      }}
      serialNumberError={serialNumberError}
      onSerialNumberError={setSerialNumberError}
      identityNumber={identityNumber}
      onIdentityNumberChange={(val) => {
        setIdentityNumberOverride(val);
        setIdentityNumberError(null);
      }}
      identityNumberError={identityNumberError}
      onIdentityNumberError={setIdentityNumberError}
      warrantyId={warrantyId}
      onWarrantySelect={(wId, sn) => {
        setWarrantyId(wId);
        if (sn) {
          setSerialNumber(sn);
          setSerialNumberError(null);
        }
      }}
      cargo={cargo}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isLoggedIn={isLoggedIn}
      isGuestMode={isGuestMode}
      onChooseGuest={() => setIsGuestMode((prev) => !prev)}
      onChooseLogin={goToLogin}
      onChooseRegister={goToRegister}
      guestFirstName={guestFirstName}
      onGuestFirstNameChange={setGuestFirstName}
      guestLastName={guestLastName}
      onGuestLastNameChange={setGuestLastName}
      guestPhoneNumber={guestPhoneNumber}
      onGuestPhoneNumberChange={setGuestPhoneNumber}
      guestEmail={guestEmail}
      onGuestEmailChange={setGuestEmail}
    />
  );

  if (completed) {
    return (
      <div className="space-y-6">
        <TalepStepper steps={steps} active={active} />
        <CompletedScreen
          requestId={createdRequestId}
          pickupDate={cargo.pickupDate}
          isTv={summary.some(
            (s) =>
              s.key === 'category' &&
              (s.value.toLowerCase().includes('tv') ||
                s.value.toLowerCase().includes('televizyon'))
          )}
        />
      </div>
    );
  }

  // İlk adımda henüz seçim yok; yan sütunda güvence paneli durur.
  const sidebar = device ? (
    <SelectionCard
      device={device}
      requestTypes={currentDisplayTypes}
      isWarrantyCovered={!!warrantyId}
      preview={
        paramKey ? undefined : cargoPreviewRows(cargo.address, cargo.pickupDate)
      }
      onEdit={(key) => setParam(key, null)}
      // Kart hangi seviyeye kadar doluysa oraya döner. Model seçilmeden
      // (marka kartı gösterilirken) 'model'e dönmek URL'i değiştirmediği için
      // "Değiştir" hiçbir şey yapmıyordu.
      deviceEditKey={
        modelId
          ? 'model'
          : summary.some((s) => s.key === 'series')
            ? 'series'
            : 'brand'
      }
      onRemoveType={paramKey === 'type' ? toggleType : undefined}
      onClearTypes={
        paramKey === 'type' ? () => setLiveSelectedTypeIds([]) : undefined
      }
    />
  ) : (
    <TrustPanel />
  );

  return (
    <div className="space-y-6">
      <TalepStepper steps={steps} active={active} />

      <div className={cn('flex flex-col gap-6 lg:flex-row lg:gap-8')}>
        <div className="min-w-0 flex-1">{stepContent}</div>
        <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-80 lg:self-start">
          {sidebar}
        </aside>
      </div>
      <CallbackRequestModal
        isOpen={!!selectedAnalysisType}
        onClose={() => setSelectedAnalysisType(null)}
        requestType={selectedAnalysisType}
        device={device}
        modelId={modelId}
      />
    </div>
  );
};

export default TalepFlow;
